import { useEffect, useState } from 'react';
import { tableOptions } from '../../engine/casino';
import { GAMES } from '../../engine/games';
import { bidChance, matches, OPPONENTS, RAKE, type LiarsDiceState } from '../../engine/games/liarsDice';
import { money } from '../../format';
import { Die } from '../parts';
import { Burst, Controls, secondChanceFrame, Stage, StatusLine, useAnimClock, useBurst, useLuckMoment, type ActionSpec, type TableProps } from './Frame';

const NAMES = ['You', ...OPPONENTS.map((o) => o.name)];
/** The reveal: each player's dice turn over in the engine's order (Rosa,
 *  Vic, you), then the count. */
const REVEAL_ORDER = [1, 2, 0];
const FLIP_MS = 600;
const COUNT_MS = 900;
const LUCK_HOLD_MS = 1500;

interface BidPick {
  count: number;
  face: number;
}

export function LiarsDiceTable({ snapshot, actions }: TableProps) {
  const run = snapshot.run!;
  const s = run.tables.liarsdice?.state as LiarsDiceState | undefined;
  const anim = snapshot.anim;
  const luckFrame = secondChanceFrame(snapshot);
  const revealing = !!anim && !!s?.reveal;
  const flips = s ? REVEAL_ORDER.filter((i) => (s.dice[i]?.length ?? 0) > 0).length : 0;
  const elapsed = useAnimClock(snapshot, actions, flips * FLIP_MS + COUNT_MS + (luckFrame ? LUCK_HOLD_MS : 0));
  const [burst, fading, setBurst] = useBurst();

  // The dice turn over and the count shows you losing a die; then the second chance.
  useLuckMoment(snapshot, elapsed, luckFrame ? flips * FLIP_MS + COUNT_MS : null, () => ({ what: 'You would have lost a die.', saved: true }), setBurst);

  // Your bid, picked with a count and a face; only legal raises can be sent.
  const options = tableOptions(snapshot);
  const legal = options.filter((o) => o.id.startsWith('bid:')).map((o) => {
    const [, c, f] = o.id.split(':').map(Number);
    return { count: c!, face: f! };
  });
  const [pick, setPick] = useState<BidPick | null>(null);
  const firstLegal = legal[0] ?? null;
  const current = pick && legal.some((b) => b.count === pick.count && b.face === pick.face) ? pick : firstLegal;
  useEffect(() => setPick(null), [s?.bid?.count, s?.bid?.face, s?.turn]);

  if (!s || s.phase === 'idle') {
    return (
      <>
        <Stage className="dice-stage">
          <div className="dice-rules">
            <p><strong>Three dice each. 1s are wild.</strong></p>
            <p>Bid how many of a face are on the whole table, or call “Liar!” on the last bid. Whoever’s wrong loses a die. Last one standing takes the pot, less {Math.round(RAKE * 100)}% for the house.</p>
            <p>You play Rosa (careful) and Vic (bluffs more). Everyone antes your bet.</p>
          </div>
        </Stage>
        <Controls snapshot={snapshot} actions={actions} />
      </>
    );
  }

  const total = s.dice.reduce((n, d) => n + d.length, 0);
  const revealed = (i: number) => {
    if (s.phase === 'done' || (s.phase === 'reveal' && !anim)) return true;
    if (!revealing) return i === 0;
    const k = REVEAL_ORDER.filter((j) => s.dice[j]!.length > 0).indexOf(i);
    return i === 0 || elapsed >= (k + 1) * FLIP_MS;
  };
  const counted = s.reveal && (!anim || elapsed >= flips * FLIP_MS);
  const face = s.reveal?.bid.face ?? s.bid?.face;
  const hot = (d: number) => !!counted && face !== undefined && (d === face || d === 1);

  const seat = (i: number) => {
    const dice = s.dice[i]!;
    const out = dice.length === 0;
    const thinking = s.phase === 'bidding' && s.turn === i && i !== 0;
    const lastBid = s.bid?.by === i && s.phase === 'bidding' ? `${s.bid.count} × ${s.bid.face}s` : null;
    return (
      <div key={i} className={`seat${out ? ' out' : ''}${s.turn === i && s.phase === 'bidding' ? ' turn' : ''}${counted && s.reveal?.loser === i ? ' loser' : ''}`}>
        <div className="seat-name">{NAMES[i]}{thinking && <span className="thinking"> is thinking…</span>}</div>
        <div className={`seat-dice${i !== 0 && !out ? " cupped" : ""}`}>
          {out ? <span className="seat-out">Out</span> : dice.map((d, k) => <Die key={k} face={d} hidden={!revealed(i)} hot={revealed(i) && hot(d)} />)}
          {/* Opponents keep their dice under a leather cup until the reveal lifts it. */}
          {i !== 0 && !out && <span className={`dice-cup${revealed(i) ? " lifted" : ""}`} aria-hidden="true" />}
        </div>
        {lastBid && <div className="bubble">{lastBid}</div>}
      </div>
    );
  };

  // The live odds, for your pick and for calling the current bid.
  const own = s.dice[0]!;
  const trueChance = current ? Math.round(bidChance(own, total, current) * 100) : null;
  const lieText = s.bid && s.turn === 0 && s.phase === 'bidding' ? GAMES.liarsdice.describe(s, undefined as never, 'liar').match(/Chance it's a lie: (\d+)%/)?.[1] : undefined;
  const myTurn = s.phase === 'bidding' && s.turn === 0 && !anim;

  const counts = [...new Set(legal.map((b) => b.count))];
  const facesFor = (count: number) => legal.filter((b) => b.count === count).map((b) => b.face);

  const extra: ActionSpec[] = [];
  if (myTurn && current) extra.push({ id: 'bid', label: `Bid ${current.count} × ${current.face}s`, primary: true, onClick: () => actions.tableOption(`bid:${current.count}:${current.face}`) });
  if (myTurn && s.bid) extra.push({ id: 'call', label: 'Liar!', onClick: () => actions.tableOption('liar') });

  let result: string | undefined;
  let tone: 'win' | 'loss' | '' = '';
  if (s.reveal && counted && !anim) {
    const r = s.reveal;
    const who = r.loser === 0 ? (r.saved ? '★ You keep your die!' : 'You lose a die.') : `${NAMES[r.loser]} loses a die.`;
    result = `${NAMES[r.caller]} called Liar! on ${r.bid.count} × ${r.bid.face}s. There were ${r.actual}. ${who}`;
    if (s.phase === 'done') {
      result += s.returned > 0 ? ` You win the pot: +${money(s.returned - s.bet)}` : ` You’re out: −${money(s.bet)}`;
      tone = s.returned > 0 ? 'win' : 'loss';
    }
  }

  return (
    <>
      <Stage className="dice-stage" onClick={anim ? actions.skip : undefined}>
        <Burst burst={burst} fading={fading} pending={false} />
        <div className="seats">{seat(1)}{seat(2)}</div>
        {/* Always two lines, in every phase, so nothing below it moves: a
            small line saying what's happening, then the bid itself. */}
        <div className="current-bid">
          {s.reveal ? (
            <>
              <span className="bid-label">{NAMES[s.reveal.caller]} called Liar!{counted ? ` · there were ${s.reveal.actual}` : ''}</span>
              <span className="bid-big">{s.reveal.bid.count} × <Die face={s.reveal.bid.face} size="1em" /></span>
            </>
          ) : s.bid ? (
            <>
              <span className="bid-label">{NAMES[s.bid.by]} bid · {total} dice on the table</span>
              <span className="bid-big">{s.bid.count} × <Die face={s.bid.face} size="1em" /></span>
            </>
          ) : (
            <>
              <span className="bid-label">{s.turn === 0 ? 'You open · ' : ''}{total} dice on the table</span>
              <span className="bid-big bid-none">1s are wild</span>
            </>
          )}
        </div>
        {seat(0)}
        {myTurn && current && (
          <div className="bid-picker">
            <div className="picker-row">
              <span className="picker-label">How many</span>
              <div className="picker-counts">
                {counts.map((c) => (
                  <button key={c} className={`pick${current.count === c ? ' on' : ''}`} onClick={() => setPick({ count: c, face: facesFor(c).includes(current.face) ? current.face : facesFor(c)[0]! })}>{c}</button>
                ))}
              </div>
            </div>
            <div className="picker-row">
              <span className="picker-label">Of</span>
              <div className="picker-faces">
                {[2, 3, 4, 5, 6].map((f) => (
                  <button key={f} className={`pick face${current.face === f ? ' on' : ''}`} disabled={!facesFor(current.count).includes(f)} onClick={() => setPick({ count: current.count, face: f })} aria-label={`${f}s`}>
                    <Die face={f} />
                  </button>
                ))}
              </div>
            </div>
            <div className="picker-odds">
              {trueChance !== null && <span>Your bid is true {trueChance}% of the time</span>}
              {lieText && <span>The last bid is a lie {lieText}%</span>}
              <span>You hold {matches(own, current.face)} that count{matches(own, current.face) === 1 ? 's' : ''}</span>
            </div>
          </div>
        )}
        <StatusLine snapshot={snapshot} result={result} tone={tone} />
      </Stage>
      <Controls snapshot={snapshot} actions={actions} hide={[...options.filter((o) => o.id.startsWith('bid:') || o.id === 'liar').map((o) => o.id)]} extra={extra} onAct={() => setBurst(null)} />
    </>
  );
}
